import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '../api/client';
import CanonicalResolution, { type Resolution } from '../components/CanonicalResolution';

// ── Types ────────────────────────────────────────────────────────────

type CorrectionStatus = 'pending' | 'approved' | 'rejected';

interface CorrectionListItem {
  correction_id: string;
  book_id: string;
  book_title: string | null;
  submitted_by: string;
  submitted_by_name: string | null;
  status: CorrectionStatus;
  created_at: string | null;
  reviewed_at: string | null;
}

interface CorrectionsResponse {
  total: number;
  page: number;
  per_page: number;
  items: CorrectionListItem[];
}

interface CorrectionDetail {
  canonical_entity_linking?: boolean;
  correction_id: string;
  book_id: string;
  book_title: string | null;
  resolves_to_book_id: string | null;
  resolves_to_book_title: string | null;
  proposed_changes: Record<string, any>;
  status: CorrectionStatus;
  submitted_by: string;
  submitted_by_name: string | null;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string | null;
}

interface AdminBookDetail {
  id: string;
  title: string;
  english_name: string | null;
  author: string | null;
  cover_url: string | null;
  cover_source: string | null;
  cover_uploaded_by: string | null;
  cover_uploaded_at: string | null;
  page_count: number | null;
  description: string | null;
  genre: string | null;
  language: string | null;
  google_books_id: string | null;
  is_active: boolean;
  canonical_book_id: string | null;
  canonical_book: { id: string; title: string } | null;
  is_sri_lankan_author: boolean | null;
  average_rating: number | null;
  price_lkr: number | null;
  is_translation: boolean | null;
  original_author: string | null;
  translator: string | null;
  original_title: string | null;
  publisher: string | null;
  created_at: string | null;
  library_count: number;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

// ── Field metadata (mirrors BookCorrectionSubmit allowlist server-side) ─

const FIELD_ORDER = [
  'title', 'author', 'english_name', 'original_title', 'publisher', 'genre',
  'language', 'page_count', 'price_lkr', 'is_sri_lankan_author',
  'is_translation', 'original_author', 'translator', 'description',
];

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  author: 'Author',
  english_name: 'English Name',
  original_title: 'Original Title',
  publisher: 'Publisher',
  genre: 'Genre',
  language: 'Language',
  page_count: 'Page Count',
  price_lkr: 'Price (LKR)',
  is_sri_lankan_author: 'Sri Lankan Author',
  is_translation: 'Is Translation',
  original_author: 'Original Author',
  translator: 'Translator',
  description: 'Description',
  cover_url: 'Cover Photo',
};

// Renders a cover URL as an actual thumbnail image, reusing the exact same
// sizing/style/onError pattern as the existing "current cover" header image
// below — never the raw URL as text. Used for BOTH the current and proposed
// sides of a cover_url comparison row, so an admin can visually compare them
// instead of reading a Cloudinary link. Falls back to the same "—" empty
// convention formatFieldValue() uses elsewhere rather than attempting to
// render a missing/empty src.
const CoverThumb: React.FC<{ url: string | null | undefined }> = ({ url }) => {
  if (!url || url.trim() === '') {
    return <span className="text-gray-400 italic">—</span>;
  }
  return (
    <img
      src={url}
      alt="Cover"
      className="w-10 h-14 object-cover rounded-md shadow-sm border border-gray-200"
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  );
};

// Renders any field value as plain text only — never dangerouslySetInnerHTML.
// Handles null / empty string / boolean / numeric / long text safely, and
// falls back gracefully for any field the frontend doesn't recognize (the
// backend allowlist is the real gate; this is just a safe display fallback).
function formatFieldValue(field: string, value: any): { text: string; muted: boolean } {
  if (value === null || value === undefined) return { text: '—', muted: true };
  if (typeof value === 'boolean') return { text: value ? 'Yes' : 'No', muted: false };
  if (typeof value === 'number') {
    return { text: field === 'price_lkr' ? `Rs. ${value.toLocaleString()}` : String(value), muted: false };
  }
  if (typeof value === 'string') {
    if (value.trim() === '') return { text: '(empty)', muted: true };
    return { text: value, muted: false };
  }
  // Defensive fallback for a shape the schema shouldn't produce — never
  // rendered as HTML, just stringified text.
  try {
    return { text: JSON.stringify(value), muted: false };
  } catch {
    return { text: String(value), muted: false };
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

// ── Toast ────────────────────────────────────────────────────────────

const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`
          flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border
          animate-slide-up cursor-pointer min-w-[280px]
          ${toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
          }
        `}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
        <button className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    ))}
  </div>
);

// ── Confirm Dialog (approve) ────────────────────────────────────────

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, title, message, confirmLabel, confirmColor, onConfirm, onCancel, loading,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={loading ? undefined : onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-slide-up">
        <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6 leading-relaxed whitespace-pre-line">{message}</p>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all ${confirmColor} disabled:opacity-50 active:scale-95`}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processing...
              </span>
            ) : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Status badge ─────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: CorrectionStatus }> = ({ status }) => {
  const styles: Record<CorrectionStatus, string> = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-green-50 text-green-700 border-green-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${styles[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// ── Spinner ──────────────────────────────────────────────────────────

const Spinner: React.FC<{ size?: string }> = ({ size = 'h-5 w-5' }) => (
  <svg className={`animate-spin ${size}`} viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// ── Main Component ──────────────────────────────────────────────────

const PER_PAGE = 20;

const Corrections: React.FC = () => {
  // ── Toasts ────────────────────────────────────────────────────────
  const toastIdRef = useRef(0);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4500);
  }, []);
  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Queue state ───────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<CorrectionStatus | ''>('pending');
  const [items, setItems] = useState<CorrectionListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState<string | null>(null);
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const fetchQueue = useCallback(async (pg: number, filter: CorrectionStatus | '') => {
    setQueueLoading(true);
    setQueueError(null);
    try {
      const params: Record<string, any> = { page: pg, per_page: PER_PAGE };
      if (filter) params.status_filter = filter;
      const res = await adminApi.get<CorrectionsResponse>('/admin/corrections', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setQueueError(detail || 'Failed to load the correction queue.');
    } finally {
      setQueueLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue(page, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const handleFilterChange = (filter: CorrectionStatus | '') => {
    setStatusFilter(filter);
    setPage(1);
  };

  // ── Detail state ──────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CorrectionDetail | null>(null);
  const [resolution, setResolution] = useState<Resolution>({});
  const [resolutionValid, setResolutionValid] = useState(true);
  useEffect(() => { setResolution({}); }, [detail?.correction_id]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [currentBook, setCurrentBook] = useState<AdminBookDetail | null>(null);
  const [bookLoading, setBookLoading] = useState(false);
  const [bookError, setBookError] = useState<string | null>(null);

  // Always fetches CURRENT state live — never reuses list-cached data.
  // Used both for the initial open and for the visibility-regain refresh.
  const loadDetail = useCallback(async (correctionId: string, opts: { silent?: boolean } = {}) => {
    if (!opts.silent) {
      setDetailLoading(true);
      setDetailError(null);
    }
    let loadedDetail: CorrectionDetail | null = null;
    try {
      const res = await adminApi.get<CorrectionDetail>(`/admin/corrections/${correctionId}`);
      loadedDetail = res.data;
      setDetail(res.data);
    } catch (err: any) {
      const detailMsg = err.response?.data?.detail;
      if (!opts.silent) setDetailError(detailMsg || 'Failed to load this correction.');
      setDetailLoading(false);
      return;
    }

    const targetId = loadedDetail.resolves_to_book_id;
    if (!targetId) {
      // Backend couldn't resolve a live target (the submission book no
      // longer exists) — there is nothing to fetch and Approve cannot
      // succeed. Surface that plainly instead of pretending.
      setCurrentBook(null);
      setBookError('The book this correction was submitted against no longer exists. Approval is not possible; consider rejecting this correction.');
      if (!opts.silent) setDetailLoading(false);
      return;
    }

    if (!opts.silent) setBookLoading(true);
    setBookError(null);
    try {
      const bookRes = await adminApi.get<AdminBookDetail>(`/admin/books/${targetId}`);
      setCurrentBook(bookRes.data);
    } catch (err: any) {
      const detailMsg = err.response?.data?.detail;
      setCurrentBook(null);
      setBookError(detailMsg || 'Failed to load the current book state for comparison.');
    } finally {
      setBookLoading(false);
      setDetailLoading(false);
    }
  }, []);

  const openDetail = (correctionId: string) => {
    if (selectedId === correctionId) {
      // Toggle closed.
      setSelectedId(null);
      setDetail(null);
      setCurrentBook(null);
      setDetailError(null);
      setBookError(null);
      setShowRejectForm(false);
      setRejectReason('');
      return;
    }
    setSelectedId(correctionId);
    setDetail(null);
    setCurrentBook(null);
    setShowRejectForm(false);
    setRejectReason('');
    loadDetail(correctionId);
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setCurrentBook(null);
    setDetailError(null);
    setBookError(null);
    setShowRejectForm(false);
    setRejectReason('');
  };

  // Refresh the comparison quietly when the admin returns to this tab after
  // stepping away mid-review — mitigates (does not eliminate) reviewing
  // against data that changed while the tab was backgrounded. The backend
  // has no staleness guarantee of its own; this only keeps what's on screen
  // as fresh as possible right up to the moment Approve/Reject is clicked.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible' && selectedId) {
        loadDetail(selectedId, { silent: true });
      }
    };
    document.addEventListener('visibilitychange', handler);
    window.addEventListener('focus', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
      window.removeEventListener('focus', handler);
    };
  }, [selectedId, loadDetail]);

  // ── Approve flow ──────────────────────────────────────────────────
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const changedFields = detail ? Object.keys(detail.proposed_changes || {}) : [];

  const handleApproveClick = () => setConfirmOpen(true);

  const handleApproveConfirm = async () => {
    if (!detail) return;
    setActionLoading(true);
    try {
      const res = await adminApi.post(`/admin/corrections/${detail.correction_id}/approve`, resolution);
      addToast(res.data?.message || 'Correction approved.', 'success');
      setConfirmOpen(false);
      closeDetail();
      fetchQueue(page, statusFilter);
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Failed to approve this correction.';
      addToast(msg, 'error');
      setConfirmOpen(false);
      // Keep the review context — refresh detail in case another admin
      // already acted on it (e.g. a 409 conflict) so the panel reflects
      // reality instead of a now-stale pending state.
      loadDetail(detail.correction_id, { silent: true });
    } finally {
      setActionLoading(false);
    }
  };

  // ── Reject flow ───────────────────────────────────────────────────
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const reasonValid = rejectReason.trim().length >= 3 && rejectReason.trim().length <= 1000;

  const handleRejectSubmit = async () => {
    if (!detail || !reasonValid) return;
    setActionLoading(true);
    try {
      const res = await adminApi.post(`/admin/corrections/${detail.correction_id}/reject`, {
        reason: rejectReason.trim(),
      });
      addToast(res.data?.message || 'Correction rejected.', 'success');
      closeDetail();
      fetchQueue(page, statusFilter);
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Failed to reject this correction.';
      addToast(msg, 'error');
      // Preserve the entered reason and keep the panel open — the admin
      // shouldn't have to retype it after a transient failure.
      loadDetail(detail.correction_id, { silent: true });
    } finally {
      setActionLoading(false);
    }
  };

  // ── Render: detail panel ─────────────────────────────────────────

  const renderDetailPanel = () => {
    if (!selectedId) return null;

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-4 animate-fade-in">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-lg font-bold text-primary">Correction Detail</h2>
          <button onClick={closeDetail} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">&times;</button>
        </div>

        {detailLoading ? (
          <div className="py-10 flex items-center justify-center text-gray-400 gap-2">
            <Spinner /> Loading correction...
          </div>
        ) : detailError ? (
          <div className="py-10 text-center">
            <p className="text-coral font-medium mb-3">{detailError}</p>
            <button
              onClick={() => loadDetail(selectedId)}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200"
            >
              Retry
            </button>
          </div>
        ) : !detail ? null : (
          <>
            {/* ── Context block ── */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5 text-sm">
              <div>
                <p className="text-gray-400 text-xs uppercase font-semibold tracking-wide mb-0.5">Correction ID</p>
                <p className="text-gray-700 font-mono text-xs break-all">{detail.correction_id}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase font-semibold tracking-wide mb-0.5">Status</p>
                <StatusBadge status={detail.status} />
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase font-semibold tracking-wide mb-0.5">Submitted By</p>
                <p className="text-gray-700">{detail.submitted_by_name || detail.submitted_by}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase font-semibold tracking-wide mb-0.5">Submitted At</p>
                <p className="text-gray-700">{formatDate(detail.created_at)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase font-semibold tracking-wide mb-0.5">Submitted Against</p>
                <p className="text-gray-700">{detail.book_title || detail.book_id}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase font-semibold tracking-wide mb-0.5">Resolves To (live)</p>
                <p className="text-gray-700">
                  {detail.resolves_to_book_title || detail.resolves_to_book_id || '—'}
                  {detail.resolves_to_book_id && detail.resolves_to_book_id !== detail.book_id && (
                    <span className="ml-1 text-xs text-amber-600 font-semibold">(merged)</span>
                  )}
                </p>
              </div>
              {detail.status !== 'pending' && (
                <>
                  <div>
                    <p className="text-gray-400 text-xs uppercase font-semibold tracking-wide mb-0.5">Reviewed By</p>
                    <p className="text-gray-700">{detail.reviewed_by_name || detail.reviewed_by || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase font-semibold tracking-wide mb-0.5">Reviewed At</p>
                    <p className="text-gray-700">{formatDate(detail.reviewed_at)}</p>
                  </div>
                </>
              )}
              {detail.status === 'rejected' && detail.rejection_reason && (
                <div className="sm:col-span-2 lg:col-span-4">
                  <p className="text-gray-400 text-xs uppercase font-semibold tracking-wide mb-0.5">Rejection Reason</p>
                  <p className="text-gray-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{detail.rejection_reason}</p>
                </div>
              )}
            </div>

            {/* ── Book-state warnings ── */}
            {bookError && (
              <div className="mb-5 px-4 py-3 rounded-xl bg-coral/10 border border-coral/30 text-coral text-sm font-medium">
                ⚠️ {bookError}
              </div>
            )}
            {currentBook && !currentBook.is_active && (
              <div className="mb-5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium">
                ⚠️ The target book is currently marked <strong>inactive</strong>.
              </div>
            )}
            {currentBook?.canonical_book && (
              <div className="mb-5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium">
                ⚠️ The target book is marked as a duplicate of "{currentBook.canonical_book.title}". Applying this correction
                will update the target book's own row, not that canonical record — confirm this is the intended book before approving.
              </div>
            )}

            {/* ── CURRENT vs PROPOSED ── */}
            <div className="mb-5">
              <div className="flex items-center gap-3 mb-3">
                {currentBook?.cover_url ? (
                  <img
                    src={currentBook.cover_url}
                    alt={currentBook.title}
                    className="w-10 h-14 object-cover rounded-md shadow-sm border border-gray-200 shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-10 h-14 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0">
                    <span className="text-gray-400 text-xs">📖</span>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-bold text-gray-800">
                    {bookLoading ? 'Loading current book state...' : (currentBook?.title || detail.resolves_to_book_title || '—')}
                  </h3>
                  <p className="text-xs text-gray-400">
                    {changedFields.length} field{changedFields.length === 1 ? '' : 's'} proposed
                  </p>
                </div>
              </div>

              {bookLoading ? (
                <div className="py-6 flex items-center justify-center text-gray-400 gap-2">
                  <Spinner /> Loading current book state...
                </div>
              ) : !currentBook ? (
                changedFields.length > 0 && (
                  <p className="text-sm text-gray-400 italic">Current values unavailable — cannot render comparison.</p>
                )
              ) : changedFields.length === 0 ? (
                <p className="text-sm text-gray-400 italic">This correction has no proposed field changes.</p>
              ) : (
                <div className="rounded-xl border border-gray-100 overflow-hidden">
                  <div className="grid grid-cols-[1fr_1fr_1fr] bg-gray-50/70 border-b border-gray-100 px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wide">
                    <span>Field</span>
                    <span>Current</span>
                    <span>Proposed</span>
                  </div>
                  {[...FIELD_ORDER.filter((f) => changedFields.includes(f)), ...changedFields.filter((f) => !FIELD_ORDER.includes(f))].map((field) => {
                    if (field === 'cover_url') {
                      // Image comparison, not text — see CoverThumb above.
                      // Never routed through formatFieldValue() for this field.
                      const proposedUrl = detail.proposed_changes.cover_url as string | null | undefined;
                      const currentUrl = currentBook?.cover_url;
                      return (
                        <div
                          key={field}
                          className="grid grid-cols-[1fr_1fr_1fr] px-4 py-3 border-b border-gray-50 last:border-b-0 items-center text-sm"
                        >
                          <span className="font-semibold text-gray-600">{FIELD_LABELS[field] || field}</span>
                          <span className="pr-3"><CoverThumb url={currentUrl} /></span>
                          <span className="pr-3"><CoverThumb url={proposedUrl} /></span>
                        </div>
                      );
                    }
                    const proposedRaw = detail.proposed_changes[field];
                    const currentRaw = (currentBook as any)[field];
                    const proposed = formatFieldValue(field, proposedRaw);
                    const current = formatFieldValue(field, currentRaw);
                    const isLong = (proposed.text.length > 60) || (current.text.length > 60);
                    return (
                      <div
                        key={field}
                        className={`grid grid-cols-[1fr_1fr_1fr] px-4 py-3 border-b border-gray-50 last:border-b-0 ${isLong ? 'items-start' : 'items-center'} text-sm`}
                      >
                        <span className="font-semibold text-gray-600">{FIELD_LABELS[field] || field}</span>
                        <span className={`pr-3 break-words ${current.muted ? 'text-gray-400 italic' : 'text-gray-700'}`}>{current.text}</span>
                        <span className="pr-3 break-words text-primary font-medium">{proposed.text}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Actions ── */}
            {detail.status === 'pending' && (
              <>
              {detail.canonical_entity_linking && <CanonicalResolution key={detail.correction_id}
                changes={detail.proposed_changes} isTranslation={detail.proposed_changes.is_translation ?? !!currentBook?.is_translation} onChange={setResolution} onValidityChange={setResolutionValid} />}
              <div className="border-t border-gray-100 pt-4">
                {!showRejectForm ? (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleApproveClick}
                      disabled={actionLoading || (detail.canonical_entity_linking && !resolutionValid) || !currentBook || changedFields.length === 0}
                      className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-accent hover:bg-accent-600 transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!currentBook ? 'Cannot approve — current book state unavailable' : undefined}
                    >
                      ✓ Approve
                    </button>
                    <button
                      onClick={() => setShowRejectForm(true)}
                      disabled={actionLoading}
                      className="px-5 py-2.5 rounded-xl text-sm font-bold text-coral border border-coral hover:bg-coral hover:text-white transition-colors active:scale-95 disabled:opacity-50"
                    >
                      ✗ Reject
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Rejection reason</label>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      disabled={actionLoading}
                      rows={3}
                      maxLength={1000}
                      placeholder="Explain why this correction is being rejected (minimum 3 characters)..."
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800
                        focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral resize-none"
                    />
                    <div className="flex items-center justify-between">
                      <span className={`text-xs ${reasonValid ? 'text-gray-400' : 'text-coral'}`}>
                        {rejectReason.trim().length}/1000 {rejectReason.trim().length < 3 && '(minimum 3 characters)'}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowRejectForm(false)}
                          disabled={actionLoading}
                          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleRejectSubmit}
                          disabled={actionLoading || !reasonValid}
                          className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-coral hover:bg-coral-600 transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {actionLoading ? (
                            <span className="flex items-center gap-2"><Spinner size="h-4 w-4" /> Rejecting...</span>
                          ) : 'Confirm Rejection'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────

  const filters: Array<{ id: CorrectionStatus | ''; label: string }> = [
    { id: 'pending', label: 'Pending' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' },
    { id: '', label: 'All' },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-800">Book Corrections</h1>
          <Link to="/corrections/sinhala" className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Sinhala corrections
          </Link>
        </div>
        <p className="text-sm text-gray-500 mt-0.5">
          Review community-submitted book metadata corrections before they apply to the global catalogue.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f.id || 'all'}
            onClick={() => handleFilterChange(f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === f.id
                ? 'bg-primary text-white shadow'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={() => fetchQueue(page, statusFilter)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white
            text-sm font-medium text-gray-600 hover:bg-gray-50 shadow-sm transition-colors active:scale-95"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Detail panel */}
      {renderDetailPanel()}

      {/* Queue table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {['Correction ID', 'Book', 'Submitter', 'Submitted', 'Status', ''].map((col) => (
                  <th key={col} className="px-4 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {queueLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 6 }).map((__, j) => (
                      <td key={j} className="px-4 py-4"><div className="h-4 bg-gray-200 rounded-lg w-3/4" /></td>
                    ))}
                  </tr>
                ))
              ) : queueError ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <span className="text-4xl">⚠️</span>
                      <p className="text-coral font-medium">{queueError}</p>
                      <button
                        onClick={() => fetchQueue(page, statusFilter)}
                        className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200"
                      >
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <span className="text-4xl">📝</span>
                      <p className="text-gray-400 font-medium">No corrections found.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr
                    key={item.correction_id}
                    onClick={() => openDetail(item.correction_id)}
                    className={`cursor-pointer hover:bg-gray-50/50 transition-colors duration-150 ${
                      selectedId === item.correction_id ? 'bg-accent/5' : ''
                    }`}
                  >
                    <td className="px-4 py-3.5">
                      <span className="text-xs font-mono text-gray-500">{item.correction_id.slice(0, 8)}…</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-sm font-semibold text-gray-800 truncate max-w-[240px]">{item.book_title || item.book_id}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-gray-600">{item.submitted_by_name || item.submitted_by}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-gray-500">{formatDate(item.created_at)}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs font-semibold text-primary">
                        {selectedId === item.correction_id ? 'Hide' : 'Review'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!queueLoading && !queueError && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
            <span className="text-sm text-gray-500">
              Showing <span className="font-semibold text-gray-700">{(page - 1) * PER_PAGE + 1}</span>
              –<span className="font-semibold text-gray-700">{Math.min(page * PER_PAGE, total)}</span>
              {' '}of <span className="font-semibold text-gray-700">{total}</span>
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium
                  bg-white border border-gray-200 text-gray-600
                  hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                  transition-colors active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Prev
              </button>
              <div className="flex items-center gap-1 px-3">
                <span className="text-sm font-bold text-primary">{page}</span>
                <span className="text-sm text-gray-400">/</span>
                <span className="text-sm text-gray-500">{totalPages}</span>
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium
                  bg-white border border-gray-200 text-gray-600
                  hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                  transition-colors active:scale-95"
              >
                Next
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Approve confirmation */}
      <ConfirmDialog
        open={confirmOpen}
        title="Approve Correction"
        message={
          detail
            ? `Apply ${changedFields.length} change${changedFields.length === 1 ? '' : 's'} to "${currentBook?.title || detail.resolves_to_book_title || detail.book_title || 'this book'}"?\n\nThis updates the global book record immediately and cannot be undone from this screen.`
            : ''
        }
        confirmLabel="Approve & Apply"
        confirmColor="bg-accent hover:bg-accent-600"
        onConfirm={handleApproveConfirm}
        onCancel={() => setConfirmOpen(false)}
        loading={actionLoading}
      />

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default Corrections;
